import {
  PrioritizationFlow,
  PrioritizationFlowFilter,
  PrioritizationFlowMinSeverity,
  PrioritizationFlowStage,
} from 'api/scan-vault/prioritizationFlow';
import { VulnerabilityViewByTabs } from 'containers/Visibility/Vulnerabilities/types';

export type FlowMeasure = 'packages' | 'cves' | 'hosts';

export const FLOW_SEVERITIES = [
  'critical',
  'high',
  'medium',
  'low',
  'unknown',
] as const;
export type FlowSeverity = typeof FLOW_SEVERITIES[number];

export const FLOW_MEASURES: Array<{
  key: FlowMeasure;
  label: string;
  unit: string;
  tab: string;
}> = [
  {
    key: 'packages',
    label: 'Packages',
    unit: 'packages',
    tab: VulnerabilityViewByTabs.Package,
  },
  {
    key: 'cves',
    label: 'CVEs',
    unit: 'CVEs',
    tab: VulnerabilityViewByTabs.CVE,
  },
  {
    key: 'hosts',
    label: 'Hosts',
    unit: 'hosts',
    tab: VulnerabilityViewByTabs.Resource,
  },
];

export interface FlowOptions {
  epssThreshold: number;
  minSeverity: PrioritizationFlowMinSeverity;
}

export interface FlowChainState extends FlowOptions {
  /** Every filter in display order; `enabled` decides which are applied. */
  order: PrioritizationFlowFilter[];
  enabled: Record<PrioritizationFlowFilter, boolean>;
  measure: FlowMeasure;
}

export const EPSS_THRESHOLDS = [0.01, 0.1, 0.5];

export const DEFAULT_FLOW_CHAIN: FlowChainState = {
  order: [
    PrioritizationFlowFilter.KnownExploited,
    PrioritizationFlowFilter.Severity,
    PrioritizationFlowFilter.Patchable,
    PrioritizationFlowFilter.Epss,
  ],
  enabled: {
    [PrioritizationFlowFilter.KnownExploited]: true,
    [PrioritizationFlowFilter.Severity]: true,
    [PrioritizationFlowFilter.Patchable]: true,
    [PrioritizationFlowFilter.Epss]: false,
  },
  epssThreshold: 0.1,
  minSeverity: 'high',
  measure: 'hosts',
};

/** Applied filters, in order: enabled filters first, keeping display order. */
export const activeFilters = (chain: FlowChainState) =>
  chain.order.filter(filter => chain.enabled[filter]);

export const formatPercent = (value: number) =>
  value >= 10
    ? `${Math.round(value)}%`
    : value >= 1
    ? `${value.toFixed(1)}%`
    : value > 0
    ? `${value.toFixed(2)}%`
    : '0%';

export const formatCount = (value: number) =>
  Math.round(value).toLocaleString('en-US');

export function filterLabel(
  filter: PrioritizationFlowFilter | 'all',
  options: FlowOptions,
) {
  switch (filter) {
    case 'all':
      return 'All open';
    case PrioritizationFlowFilter.KnownExploited:
      return 'Known exploited';
    case PrioritizationFlowFilter.Patchable:
      return 'Patch available';
    case PrioritizationFlowFilter.Epss:
      return `EPSS ≥ ${Math.round(options.epssThreshold * 100)}%`;
    case PrioritizationFlowFilter.Severity:
      return options.minSeverity === 'critical'
        ? 'Critical'
        : 'Critical + High';
  }
  return filter;
}

/** Share removed by each applied filter at its position, for one measure. */
export function stageDeltas(
  stages: PrioritizationFlowStage[],
  measure: FlowMeasure,
) {
  const deltas: Partial<Record<PrioritizationFlowFilter, number>> = {};
  for (let i = 1; i < stages.length; i++) {
    const before = stages[i - 1][measure].total;
    const after = stages[i][measure].total;
    deltas[stages[i].key as PrioritizationFlowFilter] = before
      ? (100 * (before - after)) / before
      : 0;
  }
  return deltas;
}

/**
 * Details-page query for the first `stageIndex` filters. EPSS has no
 * Details filter yet, so it is reported back instead of silently dropped.
 */
export function drillPath(
  measure: FlowMeasure,
  filters: PrioritizationFlowFilter[],
  stageIndex: number,
  options: FlowOptions,
  severity?: FlowSeverity,
) {
  const tab = FLOW_MEASURES.find(m => m.key === measure)?.tab;
  const query = [`show=Details`, `tab=${tab}`];
  let severities: string | undefined;
  let omitsEpss = false;
  for (const filter of filters.slice(0, stageIndex)) {
    if (filter === PrioritizationFlowFilter.KnownExploited)
      query.push('known_exploited=true');
    if (filter === PrioritizationFlowFilter.Patchable)
      query.push('isPatchable=yes');
    if (filter === PrioritizationFlowFilter.Severity)
      severities =
        options.minSeverity === 'critical' ? 'critical' : 'critical,high';
    if (filter === PrioritizationFlowFilter.Epss) omitsEpss = true;
  }
  if (severity && severity !== 'unknown') severities = severity;
  if (severities) query.push(`severities=${severities}`);
  return { path: `/visibility/vulnerability?${query.join('&')}`, omitsEpss };
}

export type FlowNodeKind = 'pass' | 'drop' | 'severity';
export interface FlowNode {
  name: string;
  kind: FlowNodeKind;
  count: number;
  /** Stage index the node belongs to (pass and drop nodes). */
  stage?: number;
  severity?: FlowSeverity;
}
export type FlowLinkKind = 'pass' | 'drop' | 'carry' | 'severity';
export interface FlowLink {
  source: number;
  target: number;
  value: number;
  kind: FlowLinkKind;
  /** Filter label that removed these entities (drop links). */
  removedBy?: string;
  severity?: FlowSeverity;
}

/**
 * Sankey graph for one measure. Recharts moves every sink node to the last
 * column, so removed entities ride a cumulative "Filtered out" lane that
 * reaches the final column next to the severity split.
 */
export function buildFlowGraph(
  flow: PrioritizationFlow | undefined,
  measure: FlowMeasure,
) {
  const nodes: FlowNode[] = [];
  const links: FlowLink[] = [];
  const stages = flow?.stages ?? [];
  if (!stages.length || !stages[0][measure].total) return { nodes, links };
  const options = {
    epssThreshold: flow?.epss_threshold ?? 0.1,
    minSeverity: flow?.min_severity ?? 'high',
  };
  const add = (node: FlowNode) => nodes.push(node) - 1;
  let previous = add({
    name: 'All open',
    kind: 'pass',
    count: stages[0][measure].total,
    stage: 0,
  });
  let out: number | undefined;
  let outCount = 0;
  for (let i = 1; i < stages.length; i++) {
    const before = stages[i - 1][measure].total;
    const after = stages[i][measure].total;
    const label = filterLabel(stages[i].key, options);
    let next: number | undefined;
    if (after > 0) {
      next = add({ name: label, kind: 'pass', count: after, stage: i });
      links.push({
        source: previous,
        target: next,
        value: after,
        kind: 'pass',
      });
    }
    const removed = Math.max(0, before - after);
    if (removed > 0 || out !== undefined) {
      outCount += removed;
      const node = add({
        name: 'Filtered out',
        kind: 'drop',
        count: outCount,
        stage: i,
      });
      if (removed > 0)
        links.push({
          source: previous,
          target: node,
          value: removed,
          kind: 'drop',
          removedBy: label,
        });
      if (out !== undefined)
        links.push({
          source: out,
          target: node,
          value: outCount - removed,
          kind: 'carry',
        });
      out = node;
    }
    if (next === undefined) return { nodes, links };
    previous = next;
  }
  const last = stages[stages.length - 1][measure];
  for (const severity of FLOW_SEVERITIES) {
    const value = last[severity];
    if (!value) continue;
    const node = add({
      name: severity === 'unknown' ? 'Unknown' : capitalize(severity),
      kind: 'severity',
      count: value,
      severity,
    });
    links.push({
      source: previous,
      target: node,
      value,
      kind: 'severity',
      severity,
    });
  }
  // A trailing out lane would otherwise stop one column short; extend it so
  // it lines up under the severity split.
  if (out !== undefined && links.some(link => link.kind === 'severity')) {
    const node = add({ name: 'Filtered out', kind: 'drop', count: outCount });
    links.push({ source: out, target: node, value: outCount, kind: 'carry' });
  }
  return { nodes, links };
}

/**
 * Tooltip text for a hovered Sankey node or link. Recharts hands the tooltip
 * the rendered element's props; the node or link data sits on its `payload`.
 */
export function describeFlowTooltip(
  entry: { payload?: any; value?: number } | undefined,
  graph: { nodes: FlowNode[]; links: FlowLink[] },
  unit: string,
) {
  const element = entry?.payload;
  const item = element?.payload ?? element;
  if (!item) return undefined;
  const value = Number(item.value ?? entry?.value ?? 0);
  if (item.source && item.target) {
    const link =
      item.kind !== undefined
        ? (item as FlowLink)
        : graph.links.find(
            l =>
              l.value === value &&
              graph.nodes[l.source]?.name === item.source.name &&
              graph.nodes[l.target]?.count === item.target.count,
          );
    const sourceValue = Number(item.source.value ?? item.source.count ?? 0);
    const share = sourceValue
      ? formatPercent((100 * value) / sourceValue)
      : '0%';
    if (link?.kind === 'carry')
      return {
        title: 'Already filtered out',
        detail: `${formatCount(value)} ${unit} removed by earlier filters`,
      };
    if (link?.kind === 'drop')
      return {
        title: `Removed by ${link.removedBy}`,
        detail: `${formatCount(value)} ${unit} · ${share} of ${String(
          item.source.name ?? '',
        ).toLowerCase()}`,
      };
    return {
      title: `${item.source.name} → ${item.target.name}`,
      detail: `${formatCount(value)} ${unit} · ${share} kept`,
    };
  }
  return {
    title: item.name,
    detail: `${formatCount(Number(item.count ?? value))} ${unit}`,
  };
}

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

const STORAGE_KEY = 'ariksa.vulnerability.prioritizationFlow.v1';

export function loadFlowChain(): FlowChainState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_FLOW_CHAIN;
    const saved = JSON.parse(raw) as Partial<FlowChainState>;
    const known = new Set(DEFAULT_FLOW_CHAIN.order);
    const order = (saved.order ?? []).filter(filter => known.has(filter));
    if (order.length !== known.size) return DEFAULT_FLOW_CHAIN;
    return {
      order,
      enabled: { ...DEFAULT_FLOW_CHAIN.enabled, ...saved.enabled },
      epssThreshold: EPSS_THRESHOLDS.includes(saved.epssThreshold as number)
        ? (saved.epssThreshold as number)
        : DEFAULT_FLOW_CHAIN.epssThreshold,
      minSeverity: saved.minSeverity === 'critical' ? 'critical' : 'high',
      measure: FLOW_MEASURES.some(m => m.key === saved.measure)
        ? (saved.measure as FlowMeasure)
        : DEFAULT_FLOW_CHAIN.measure,
    };
  } catch {
    return DEFAULT_FLOW_CHAIN;
  }
}

export function saveFlowChain(chain: FlowChainState) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(chain));
  } catch {
    // Storage can be unavailable (private mode); the widget still works.
  }
}
